import { forwardRef, type ComponentPropsWithoutRef } from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../lib/utils";

const emptyMessageVariants = cva("text-sm text-muted-foreground", {
  variants: {
    variant: {
      panel: "rounded-lg border border-dashed border-border-subtle",
      plain: "",
    },
    size: {
      compact: "",
      default: "",
      spacious: "",
    },
    tone: {
      plain: "",
      muted: "bg-muted/30",
      subtle: "bg-background/50",
      canvas: "bg-surface-canvas/60",
    },
    align: {
      start: "text-start",
      center: "text-center",
    },
  },
  compoundVariants: [
    { variant: "panel", size: "compact", className: "px-4 py-5" },
    { variant: "panel", size: "default", className: "px-4 py-6" },
    { variant: "panel", size: "spacious", className: "px-5 py-8" },
  ],
  defaultVariants: { variant: "panel", size: "default", tone: "plain" },
});

type EmptyMessageProps = ComponentPropsWithoutRef<"p"> &
  VariantProps<typeof emptyMessageVariants>;

/** Use plain inside an existing section; the section owns its spacing. Use Empty for titles and actions. */
const EmptyMessage = forwardRef<HTMLParagraphElement, EmptyMessageProps>(
  function EmptyMessage(
    { variant = "panel", size, tone, align, className, ...props },
    ref,
  ) {
    return (
      <p
        ref={ref}
        className={cn(
          emptyMessageVariants({
            variant,
            size,
            tone,
            align: align ?? (variant === "plain" ? undefined : "center"),
          }),
          className,
        )}
        {...props}
      />
    );
  },
);

function Empty({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="empty"
      className={cn(
        "flex min-w-0 flex-1 flex-col items-center justify-center gap-6 rounded-lg border-dashed p-6 text-center text-balance md:p-12",
        className,
      )}
      {...props}
    />
  );
}

function EmptyHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="empty-header"
      className={cn(
        "flex max-w-sm flex-col items-center gap-2 text-center",
        className,
      )}
      {...props}
    />
  );
}

const emptyMediaVariants = cva(
  "flex shrink-0 items-center justify-center mb-2 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-transparent",
        icon: "bg-muted text-foreground flex size-10 shrink-0 items-center justify-center rounded-lg [&_svg:not([class*='size-'])]:size-6",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

function EmptyMedia({
  className,
  variant = "default",
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof emptyMediaVariants>) {
  return (
    <div
      data-slot="empty-icon"
      data-variant={variant}
      className={cn(emptyMediaVariants({ variant, className }))}
      {...props}
    />
  );
}

function EmptyTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="empty-title"
      className={cn("text-lg font-medium tracking-tight", className)}
      {...props}
    />
  );
}

function EmptyDescription({ className, ...props }: React.ComponentProps<"p">) {
  return (
    <div
      data-slot="empty-description"
      className={cn(
        "text-muted-foreground [&>a:hover]:text-primary text-sm/relaxed [&>a]:underline [&>a]:underline-offset-4",
        className,
      )}
      {...props}
    />
  );
}

function EmptyContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="empty-content"
      className={cn(
        "flex w-full max-w-sm min-w-0 flex-col items-center gap-4 text-sm text-balance",
        className,
      )}
      {...props}
    />
  );
}

export {
  Empty,
  EmptyMessage,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
};
